/**
 * The signed-in layout (container): feeds the session, the backend, the
 * current route and the theme into the presentational `AppShell`, and turns
 * its events into navigation, theme changes and sign-out.
 */
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { suspendedMessage } from '../api/suspended.js';
import { AnnouncementBanner } from '../components/AnnouncementBanner.js';
import { AppShell, type NavItem } from '../components/AppShell.js';
import { TenantSuspended } from '../components/TenantSuspended.js';
import { AlertBadge } from '../components/alerts/AlertBadge.js';
import { useAlerts, useAnnouncements, useDismissals, useFirstRunFacts, useGrants, useLiveChanges, useMe, useOnboarding, usePrimaryId, useServices, useSession, useTheme } from '../app/hooks.js';
import { showsAdmin } from '../lib/admin/sections.js';
import { dismissalKey, undismissed } from '../lib/dismissals.js';
import { shouldAutoOpen } from '../lib/onboarding.js';
import { type Grants, allows } from '../lib/permissions.js';
import { otherTheme } from '../lib/theme.js';
import { vendorAccessOf } from '../lib/vendor.js';
import { ConnectivityControl } from './ConnectivityControl.js';

/**
 * The top bar's pages. `requires: 'vendor'` is shown only to a vendor (`vendorAccessOf`: `vendor.ops` or
 * `vendor.admin` tenant-wide, never on a node); `requires: 'admin'` (the Admin area, Phase 7b) to whoever may see one
 * of its sections (`showsAdmin`, `lib/admin/sections.ts`);
 * `requires: 'tenantAdmin'` only to a tenant admin (or where grants are unknown: a node).
 */
const NAV: readonly { readonly label: string; readonly href: string; readonly requires?: 'vendor' | 'admin' | 'tenantAdmin' }[] = [
  { label: 'Rundowns', href: '/rundowns' },
  { label: 'Productions', href: '/productions' },
  { label: 'Cloud', href: '/cloud-productions' },
  { label: 'Studios', href: '/studios' },
  { label: 'Show styles', href: '/show-styles' },
  { label: 'Alerts', href: '/alerts' },
  // The onboarding wizard, re-opened (phase 7b).
  { label: 'Setup', href: '/onboarding', requires: 'tenantAdmin' },
  { label: 'Who am I', href: '/whoami' },
  { label: 'Account', href: '/account' },
  { label: 'Admin', href: '/admin', requires: 'admin' },
  { label: 'Vendor', href: '/vendor', requires: 'vendor' },
];

export const ONBOARDING_HREF = '/onboarding';

/** A tenant admin, as far as the backend says (unknown grants — a node — offer the wizard, which works there too). */
function isTenantAdmin(grants: Grants): boolean {
  return allows(grants, 'tenant.admin', { siteId: null, studioId: null });
}

/**
 * The first run (phase 7b): a tenant admin whose tenant is empty and who never opened the wizard is taken to it,
 * once. Only where progress is saved (the cloud) and the grants are known: a node never opens it unasked.
 */
function useFirstRun(grants: Grants, pathname: string): void {
  const admin = grants !== null && isTenantAdmin(grants);
  const onboarding = useOnboarding(admin);
  const progress = onboarding.data?.persisted === true ? onboarding.data.onboarding : null;
  const facts = useFirstRunFacts(progress?.status === 'not-started');
  const navigate = useNavigate();
  const opened = useRef(false);
  useEffect(() => {
    if (opened.current || facts === null || pathname === ONBOARDING_HREF) return;
    if (!shouldAutoOpen(progress, { ...facts, onPremSiteIds: null, cloudProductionCount: null }, admin)) return;
    opened.current = true;
    void navigate({ to: ONBOARDING_HREF });
  }, [facts, pathname, progress, admin, navigate]);
}

/** `/rundowns/<id>` (the editor) and `/productions/<id>` (the playout view) fill the page under the top bar. */
function isEditorPath(pathname: string): boolean {
  return /^\/(rundowns|productions)\/[^/]+/.test(pathname);
}

export function AppLayout() {
  const services = useServices();
  const session = useSession();
  const theme = useTheme();
  const pathname = useLocation({ select: (l) => l.pathname });
  const navigate = useNavigate();
  // Subscribed so the backend badge follows a failover (`services.backend` names the current primary) — and only that.
  usePrimaryId();
  // The tenant change channel (D35): every list and open editor hears every change, from anyone, while signed in.
  useLiveChanges();
  const me = useMe();
  const grants = useGrants();
  const announcements = useAnnouncements();
  const dismissed = useDismissals();
  const alerts = useAlerts('active');
  useFirstRun(grants, pathname);

  // The guard (router.tsx) keeps signed-out users off this layout; a session
  // that ends while it is shown re-runs the guard (start.tsx).
  if (session === null) return null;

  // A suspended tenant (phase 7b): every request answers `suspended`; say so instead of a broken app.
  const suspended = suspendedMessage(me.error) ?? suspendedMessage(announcements.error);
  if (suspended !== null) return <TenantSuspended message={suspended} tenantId={session.tenantId} onSignOut={() => services.sessions.signOut()} />;

  const vendor = vendorAccessOf(grants);
  const admin = showsAdmin(grants);
  const visible = (requires: 'vendor' | 'admin' | 'tenantAdmin' | undefined): boolean => {
    if (requires === undefined) return true;
    if (requires === 'vendor') return vendor.canSee;
    return requires === 'admin' ? admin : isTenantAdmin(grants);
  };
  const nav: NavItem[] = NAV.filter((item) => visible(item.requires)).map((item) => ({ label: item.label, href: item.href, active: pathname === item.href || pathname.startsWith(`${item.href}/`) }));
  const shown = undismissed(announcements.data ?? [], dismissed);
  return (
    <AppShell
      backend={{ kind: services.backend.kind, label: services.backend.label }}
      user={{ userId: session.userId, email: session.email, tenantId: session.tenantId }}
      nav={nav}
      onNavigate={(href) => {
        void navigate({ to: href });
      }}
      theme={theme}
      onToggleTheme={() => services.theme.set(otherTheme(theme))}
      // Signing out is enough: start.tsx re-runs the guard whenever the session ends.
      onSignOut={() => services.sessions.signOut()}
      status={<ConnectivityControl />}
      alerts={
        <AlertBadge
          counts={alerts.data?.available === true ? alerts.data.counts : null}
          href="/alerts"
          onNavigate={(href) => {
            void navigate({ to: href });
          }}
        />
      }
      fullBleed={isEditorPath(pathname)}
      banner={<AnnouncementBanner announcements={shown} onDismiss={(a) => services.dismissals.dismiss(dismissalKey(a))} />}
    >
      <Outlet />
    </AppShell>
  );
}
